import { normalizeSeverity } from '../constants/colors';
import { getRiskLevel } from './risk';

const STORAGE_KEY = 'corpintel_notifications';
const READ_KEY = 'corpintel_notifications_read';
const MAX_ITEMS = 40;
const HIGH_RISK_ID = 'high-risk-24h';

function loadJson(key, fallback) {
  try {
    const raw = localStorage.getItem(key);
    return raw ? JSON.parse(raw) : fallback;
  } catch {
    return fallback;
  }
}

function saveJson(key, value) {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch {
    // storage unavailable
  }
}

export function getStoredNotifications() {
  return loadJson(STORAGE_KEY, []);
}

export function getReadNotificationIds() {
  return new Set(loadJson(READ_KEY, []));
}

export function markNotificationRead(id) {
  const read = getReadNotificationIds();
  read.add(id);
  saveJson(READ_KEY, [...read]);
}

export function markAllNotificationsRead(ids) {
  const read = getReadNotificationIds();
  ids.forEach(id => read.add(id));
  saveJson(READ_KEY, [...read]);
}

export function logNotification(entry) {
  const item = {
    id: entry.id || `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    createdAt: new Date().toISOString(),
    read: false,
    ...entry,
  };
  const stored = getStoredNotifications().filter(n => n.id !== item.id);
  const next = [item, ...stored].slice(0, MAX_ITEMS);
  saveJson(STORAGE_KEY, next);
  return item;
}

function countHighRiskLast24h(records) {
  const cutoff = Date.now() - 24 * 60 * 60 * 1000;
  return (records || []).filter(r => {
    if (!r.published_at || new Date(r.published_at).getTime() < cutoff) return false;
    const sev = normalizeSeverity(r.severity);
    return sev === 'critical' || sev === 'high' || getRiskLevel(r.severity) === 'high';
  }).length;
}

/**
 * Merge persisted events with a live high-risk summary for the bell feed.
 */
export function buildNotificationFeed(records = []) {
  const readIds = getReadNotificationIds();
  const stored = getStoredNotifications();
  const highRiskCount = countHighRiskLast24h(records);
  const feed = [];

  if (highRiskCount > 0) {
    feed.push({
      id: HIGH_RISK_ID,
      type: 'high_risk',
      title: `${highRiskCount} new high-risk alert${highRiskCount === 1 ? '' : 's'}`,
      message: 'Published in the last 24 hours — review now',
      createdAt: new Date().toISOString(),
      action: 'view_high_risk',
      unread: !readIds.has(HIGH_RISK_ID),
    });
  }

  stored.forEach(n => {
    feed.push({
      ...n,
      unread: !readIds.has(n.id),
    });
  });

  return feed.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
}

export function getUnreadNotificationCount(feed) {
  return (feed || []).filter(n => n.unread).length;
}

export function notifyBriefSent(activity) {
  if (activity?.type === 'executive_brief_sent') {
    return logNotification({
      type: 'executive_brief_sent',
      title: 'Executive brief sent',
      message: `Today's brief sent to ${activity?.recipient || 'team'}`,
      action: 'open_email',
      meta: { activityId: activity?.id },
    });
  }

  return logNotification({
    type: 'brief_sent',
    title: 'Brief sent',
    message: activity?.subject || `Brief for ${activity?.company || 'alert'}`,
    action: 'open_email',
    meta: { activityId: activity?.id },
  });
}

export function notifyCompanyResultsReady(companyName, recordsAdded, chatId) {
  if (!companyName) return null;
  const count = Number(recordsAdded) || 0;
  return logNotification({
    type: 'company_results_ready',
    title: `Results ready for ${companyName}`,
    message: count > 0
      ? `First collection finished - ${count} new record${count === 1 ? '' : 's'} collected`
      : 'First collection finished, but nothing new was found in the sources',
    // The answer was posted into the chat that asked for this company, so send
    // the user back there rather than to the generic Intelligence list.
    action: 'open_chat',
    meta: { company: companyName, chatId },
  });
}

export function notifyCompanyAdded(companyName) {
  if (!companyName) return null;
  return logNotification({
    type: 'company_added',
    title: 'New company added',
    message: `${companyName} is now being monitored - first news sweep is running, records appear in a few minutes`,
    action: 'view_company',
    meta: { company: companyName },
  });
}